import { useCallback, useEffect, useRef, useState } from "react";
import type { BrowserSpeechRecognition, SpeechRecognitionEventLike } from "./speech-recognition";
import { getSpeechRecognitionConstructor } from "./speech-recognition";

/** Lifecycle of a Web Speech API based speech-to-text control. */
export const SPEECH_TO_TEXT_STATUS = {
  ARMING: "ARMING",
  IDLE: "IDLE",
  RECORDING: "RECORDING",
  FINALIZING: "FINALIZING",
  ERROR: "ERROR",
} as const;

export type SpeechToTextStatus = (typeof SPEECH_TO_TEXT_STATUS)[keyof typeof SPEECH_TO_TEXT_STATUS];

export type UseSpeechToTextOptions = {
  /** BCP 47 language tag passed to the recognizer. Defaults to the browser's language. */
  lang?: string;
  /** Fires once per finalized speech result; interim (not-yet-final) results are ignored. */
  onFinalTranscript: (transcript: string) => void;
  /** Fires after recognition has fully stopped. */
  onEnd?: () => void;
};

/**
 * Wraps the browser Web Speech API for a press-to-start/press-to-stop microphone control.
 *
 * Recognition restarts are not supported: `onend` always resolves to idle (unless an error is
 * already showing), which matches a simple toggle button rather than continuous dictation.
 */
export const useSpeechToText = ({ lang, onFinalTranscript, onEnd }: UseSpeechToTextOptions) => {
  const [isSupported, setIsSupported] = useState(false);
  const [status, setStatus] = useState<SpeechToTextStatus>(SPEECH_TO_TEXT_STATUS.IDLE);
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const finishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ignoreNextErrorRef = useRef(false);
  const onFinalTranscriptRef = useRef(onFinalTranscript);
  const onEndRef = useRef(onEnd);
  onFinalTranscriptRef.current = onFinalTranscript;
  onEndRef.current = onEnd;

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Synchronizes the browser SpeechRecognition API, which lives outside React.
  useEffect(() => {
    const SpeechRecognitionCtor = getSpeechRecognitionConstructor();

    setIsSupported(Boolean(SpeechRecognitionCtor));

    if (!SpeechRecognitionCtor) {
      recognitionRef.current = null;
      return undefined;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang || window.navigator.language || "en-US";
    recognition.onstart = () => {
      ignoreNextErrorRef.current = false;
      setStatus((current) =>
        current === SPEECH_TO_TEXT_STATUS.FINALIZING ? current : SPEECH_TO_TEXT_STATUS.RECORDING,
      );
    };
    recognition.onresult = (event: SpeechRecognitionEventLike) => {
      let finalTranscript = "";

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];

        if (result?.isFinal) {
          finalTranscript += result[0]?.transcript ?? "";
        }
      }

      if (finalTranscript) {
        onFinalTranscriptRef.current(finalTranscript);
      }
    };
    recognition.onerror = () => {
      if (ignoreNextErrorRef.current) {
        ignoreNextErrorRef.current = false;
        return;
      }
      setStatus(SPEECH_TO_TEXT_STATUS.ERROR);
    };
    recognition.onend = () => {
      if (finishTimerRef.current) {
        clearTimeout(finishTimerRef.current);
        finishTimerRef.current = null;
      }
      setStatus((current) =>
        current === SPEECH_TO_TEXT_STATUS.ERROR ? current : SPEECH_TO_TEXT_STATUS.IDLE,
      );
      onEndRef.current?.();
    };
    recognitionRef.current = recognition;

    return () => {
      recognition.onstart = null;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.abort();
      if (finishTimerRef.current) {
        clearTimeout(finishTimerRef.current);
        finishTimerRef.current = null;
      }
      recognitionRef.current = null;
    };
  }, [lang]);

  const toggle = useCallback(() => {
    const recognition = recognitionRef.current;

    if (!recognition) {
      return;
    }

    if (status === SPEECH_TO_TEXT_STATUS.RECORDING || status === SPEECH_TO_TEXT_STATUS.ARMING) {
      recognition.stop();
      return;
    }

    try {
      setStatus(SPEECH_TO_TEXT_STATUS.ARMING);
      recognition.start();
    } catch {
      setStatus(SPEECH_TO_TEXT_STATUS.ERROR);
    }
  }, [status]);

  const start = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      return;
    }
    try {
      setStatus(SPEECH_TO_TEXT_STATUS.ARMING);
      recognition.start();
    } catch {
      setStatus(SPEECH_TO_TEXT_STATUS.ERROR);
    }
  }, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setStatus(SPEECH_TO_TEXT_STATUS.IDLE);
  }, []);

  const finish = useCallback((delayMs = 2000) => {
    if (!recognitionRef.current || finishTimerRef.current) {
      return;
    }
    setStatus(SPEECH_TO_TEXT_STATUS.FINALIZING);
    finishTimerRef.current = setTimeout(() => {
      finishTimerRef.current = null;
      recognitionRef.current?.stop();
    }, delayMs);
  }, []);

  const cancel = useCallback(() => {
    if (finishTimerRef.current) {
      clearTimeout(finishTimerRef.current);
      finishTimerRef.current = null;
    }
    ignoreNextErrorRef.current = true;
    recognitionRef.current?.abort();
    setStatus(SPEECH_TO_TEXT_STATUS.IDLE);
  }, []);

  return { cancel, finish, isSupported, start, status, stop, toggle };
};
